import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT, Field, SecondaryButton, inputClass, ringStyle } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { MESSAGES, trackApi, trackErrorMessage } from "./trackApi";

// Artist Studio > Monetization > Entitlement usage log (FR-ENT-003): every licence bought on the artist's catalogue and its
// current status. The buyer is shown by @handle only (the API never sends an email or payment detail). Filters (track,
// class, status, date range) work alone and together; rows come in cursor pages; Export CSV follows the same filters, and a
// large export is emailed as a link instead of downloaded.

export const CLASS_OPTIONS = [
  { value: "personal", label: "Personal" },
  { value: "creator", label: "Creator" },
  { value: "business", label: "Business" },
];
export const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
  { value: "refunded", label: "Refunded" },
];
// Active green, Expired grey, Revoked red, Refunded grey.
export const STATUS_PILL = {
  active: "bg-green-100 text-green-800",
  expired: "bg-gray-100 text-gray-600",
  revoked: "bg-red-100 text-red-700",
  refunded: "bg-gray-200 text-gray-700",
};
const EMPTY_FILTERS = { track_id: "", usage_class: "", status: "", from: "", to: "" };

export const formatPurchased = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
};

// The message to show under the date boxes, or "" when the range is fine. Checked on every change.
export function dateRangeError(from, to) {
  if (from && to && from > to) return "The start date must be on or before the end date.";
  return "";
}

// Only the filters that are set, as the API's query.
export function filterQuery(filters) {
  const query = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) query[key] = value;
  });
  return query;
}

export function StatusPill({ status, label }) {
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${STATUS_PILL[status] || STATUS_PILL.expired}`} data-testid="usage-status">
      {label || status}
    </span>
  );
}

// Reads the message out of an error whose body is a Blob (the CSV request asks for a blob).
async function exportErrorMessage(e) {
  const data = e?.response?.data;
  if (data && typeof data.text === "function" && [400, 409].includes(e.response.status)) {
    try {
      const body = JSON.parse(await data.text());
      if (body?.message) return body.message;
    } catch (err) {
      // fall through to the standard message
    }
  }
  return trackErrorMessage(e, MESSAGES[500]);
}

function saveFile(blob, name) {
  const href = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
}

function UsageLogBody() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [total, setTotal] = useState(null);
  const [trackOptions, setTrackOptions] = useState([]);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [notice, setNotice] = useState("");
  const latest = useRef(0);

  const rangeError = dateRangeError(filters.from, filters.to);
  const filtered = Object.values(filters).some(Boolean);

  const loadFirst = useCallback((active) => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    setError("");
    setRecords(null);
    trackApi.usageLog(filterQuery(active)).then(
      (result) => {
        if (ticket !== latest.current) return; // a newer filter was chosen meanwhile
        setRecords(result?.records || []);
        setNextCursor(result?.has_more ? result.next_cursor : null);
        setTotal(typeof result?.total === "number" ? result.total : null);
        if (result?.filter_options?.tracks) setTrackOptions(result.filter_options.tracks);
      },
      (e) => {
        if (ticket !== latest.current) return;
        setError(trackErrorMessage(e, MESSAGES[500]));
        setRecords([]);
        setNextCursor(null);
        setTotal(null);
      }
    );
  }, []);

  // Runs on the first visit and whenever a filter changes. A date range the wrong way round is not sent.
  useEffect(() => {
    if (rangeError) return;
    loadFirst(filters);
  }, [filters, rangeError, loadFirst]);

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    const ticket = latest.current;
    setLoadingMore(true);
    trackApi.usageLog({ ...filterQuery(filters), cursor: nextCursor }).then(
      (result) => {
        if (ticket !== latest.current) return;
        setRecords((current) => [...(current || []), ...(result?.records || [])]);
        setNextCursor(result?.has_more ? result.next_cursor : null);
        setLoadingMore(false);
      },
      (e) => {
        if (ticket !== latest.current) return;
        setError(trackErrorMessage(e, MESSAGES[500]));
        setLoadingMore(false);
      }
    );
  };

  const setFilter = (key) => (event) => {
    setNotice("");
    setFilters((f) => ({ ...f, [key]: event.target.value }));
  };

  const exportCsv = async () => {
    if (exporting || rangeError) return;
    setExporting(true);
    setNotice("");
    setError("");
    try {
      const response = await trackApi.exportUsageLog(filterQuery(filters));
      if (response.status === 202) {
        let message = "Your export is being prepared. We will email you a download link when it is ready.";
        try {
          message = JSON.parse(await response.data.text())?.result?.message || message;
        } catch (err) {
          // keep the default wording
        }
        setNotice(message);
      } else {
        saveFile(response.data, `entitlement-usage-log-${new Date().toISOString().slice(0, 10)}.csv`);
        setNotice("Your CSV file was downloaded.");
      }
    } catch (e) {
      setError(await exportErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setNotice("");
    setFilters(EMPTY_FILTERS);
  };

  return (
    <div data-testid="usage-log">
      <Link to="/artist-studio/monetization" className="text-sm font-semibold text-gray-700 hover:underline">← Back to Monetization</Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Entitlement usage log</h2>
          <p className="text-gray-600 mt-1 max-w-3xl">Every licence purchased against your catalogue and its current status.</p>
        </div>
        <SecondaryButton onClick={exportCsv} disabled={exporting || Boolean(rangeError) || records === null || total === 0} data-testid="usage-export">
          {exporting ? "Exporting…" : "Export CSV"}
        </SecondaryButton>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="usage-filters">
        <Field label="Track">
          <select value={filters.track_id} onChange={setFilter("track_id")} className={inputClass(false)} style={ringStyle} data-testid="filter-track" aria-label="Track">
            <option value="">All tracks</option>
            {trackOptions.map((t) => <option key={t.id} value={t.id}>{t.title || `Track #${t.id}`}</option>)}
          </select>
        </Field>
        <Field label="Class">
          <select value={filters.usage_class} onChange={setFilter("usage_class")} className={inputClass(false)} style={ringStyle} data-testid="filter-class" aria-label="Class">
            <option value="">All classes</option>
            {CLASS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select value={filters.status} onChange={setFilter("status")} className={inputClass(false)} style={ringStyle} data-testid="filter-status" aria-label="Status">
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="From" error={rangeError}>
          <input type="date" value={filters.from} max={filters.to || undefined} onChange={setFilter("from")} className={inputClass(Boolean(rangeError))} style={ringStyle} data-testid="filter-from" aria-label="From date" />
        </Field>
        <Field label="To">
          <input type="date" value={filters.to} min={filters.from || undefined} onChange={setFilter("to")} className={inputClass(Boolean(rangeError))} style={ringStyle} data-testid="filter-to" aria-label="To date" />
        </Field>
      </div>

      {error && (
        <p className="mt-4 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert" data-testid="usage-error">
          {error} <button type="button" className="underline font-semibold" onClick={() => { setError(""); if (!rangeError) loadFirst(filters); }}>Try again</button>
        </p>
      )}
      {notice && <p className="mt-4 rounded-xl bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm" role="status" data-testid="usage-notice">{notice}</p>}

      {filtered && (
        <p className="mt-4 text-sm text-gray-600" data-testid="usage-summary">
          {total !== null && !rangeError ? `${total.toLocaleString()} ${total === 1 ? "licence" : "licences"} · ` : ""}
          <button type="button" className="underline font-semibold" onClick={clearFilters} data-testid="usage-clear">Clear filters</button>
        </p>
      )}

      <div className="mt-4 rounded-2xl border border-gray-200 bg-white overflow-x-auto">
        {records === null && !rangeError ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : rangeError ? (
          <p className="p-6 text-center text-gray-500">Fix the date range to see licences.</p>
        ) : records.length === 0 ? (
          <p className="p-6 text-center text-gray-500" data-testid="usage-log-empty">
            {filtered ? "No licences match these filters." : "No licences have been bought on your catalogue yet."}
          </p>
        ) : (
          <table className="w-full text-left text-[15px]">
            <thead className="bg-[#f1f6f2] text-xs font-bold uppercase tracking-wide" style={{ color: ACCENT }}>
              <tr>
                <th className="px-5 py-3">Track</th>
                <th className="px-5 py-3">Buyer</th>
                <th className="px-5 py-3">Class</th>
                <th className="px-5 py-3">Term</th>
                <th className="px-5 py-3">Purchased</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Posts using it</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-t border-gray-100" data-testid="usage-row">
                  <td className="px-5 py-3 font-semibold text-gray-900">{r.track_title}</td>
                  <td className="px-5 py-3 text-gray-700">
                    {r.buyer?.deleted || !r.buyer?.user_id ? (
                      <span className="text-gray-500" data-testid="usage-buyer-deleted">Deleted user</span>
                    ) : (
                      <Link to={`/profile/${r.buyer.user_id}`} className="font-medium underline" style={{ color: ACCENT }} data-testid="usage-buyer">{r.buyer.label}</Link>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-700">{r.usage_class_label}</td>
                  <td className="px-5 py-3 text-gray-700 whitespace-nowrap">{r.term_label}</td>
                  <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{formatPurchased(r.purchased_at)}</td>
                  <td className="px-5 py-3"><StatusPill status={r.status} label={r.status_label} /></td>
                  <td className="px-5 py-3 text-right text-gray-700">{r.posts_count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {nextCursor && !rangeError && (
        <div className="mt-5 flex justify-center">
          <SecondaryButton onClick={loadMore} disabled={loadingMore} data-testid="usage-load-more">{loadingMore ? "Loading…" : "Load more"}</SecondaryButton>
        </div>
      )}
    </div>
  );
}

export default function UsageLog() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="monetization" heading="" tabs={[]} maxWidth="max-w-6xl">
      <UsageLogBody />
    </StudioGate>
  );
}
